import { request } from "$/lib/api.ts"
import addonOrderQueries from "$/lib/queries/addon-order-queries.ts"
import {
  BulkMarkInvoiced,
  BulkUpdateAssignedMechanic,
  BulkUpdateHandInDate,
  BulkUpdateStatus,
  CompanyJobCounts,
  CompanyJobFilterOptions,
  CompanyJobsInvoicedFilter,
  CompanyJobsPage,
  CompanyJobsStatusFilterMode,
  CompanyJobsStatusFilterValue,
  DateUtils,
  ExtendedJob,
  JobReminder,
  JobSearchFilter,
  JobStatus,
  JobStoreAccess,
  JobSummary,
  JobTrait,
  JobsListResult,
  PickupReminderOverview,
  Route,
  SetJobStatus,
  UpdateLeasingId,
  UpdateReference,
  populateRoute,
} from "@repo/common"
import { toast } from "@repo/ui/hooks/use-toast"
import { useTranslateApiError } from "@repo/ui/lib/translate-api-error"
import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslations } from "use-intl"

interface JobFilters {
  page: number
  size: number
  startDate?: Date
  endDate?: Date
  statusList?: (JobStatus | JobTrait)[]
  customerName?: string
  physicalTag?: string
}

const fetchJobs = async (
  storeId: string,
  filters: JobFilters,
): Promise<JobsListResult> => {
  const {
    page,
    size,
    startDate,
    endDate,
    statusList,
    customerName,
    physicalTag,
  } = filters

  const params: Record<string, string | number> = {
    page,
    size,
  }

  if (startDate) {
    params.startDate = startDate.toISOString()
  }

  if (endDate) {
    params.endDate = endDate.toISOString()
  }

  if (statusList && statusList.length > 0) {
    params.statusStringified = JSON.stringify(statusList)
  }

  if (customerName) {
    params.customerName = customerName
  }

  if (physicalTag) {
    params.physicalTag = physicalTag
  }

  const res = await request<JobsListResult>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_MANY_BY_STORE_ID, storeId),
    params,
    method: "GET",
  })

  return {
    totalPages: res.totalPages,
    jobs: res.jobs.map((job) => parseJobDates(job)),
  }
}

async function findOneJobById(id: string): Promise<ExtendedJob> {
  const job = await request<ExtendedJob>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_ONE_EXTENDED_AS_STORE, id),
    method: "GET",
  })

  return parseJobDates(job)
}

async function findJobStoreAccess(id: string): Promise<JobStoreAccess> {
  return await request<JobStoreAccess>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_STORE_ACCESS, id),
    method: "GET",
  })
}

async function fetchPickupReminders(
  jobId: string,
): Promise<PickupReminderOverview> {
  return await request<PickupReminderOverview>({
    url: populateRoute(Route.JOB_CONTROLLER__GET_PICKUP_REMINDERS, jobId),
    method: "GET",
  })
}

async function setJobStatus(jobId: string, data: SetJobStatus): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__SET_JOB_STATUS, jobId),
    method: "PATCH",
    data,
  })
}

async function setIsTemporarilyHandedOut(
  jobId: string,
  isTemporarilyHandedOut: boolean,
) {
  return await request<void>({
    url: populateRoute(
      Route.JOB_CONTROLLER__SET_IS_TEMPORARILY_HANDED_OUT,
      jobId,
    ),
    method: "PATCH",
    data: { isTemporarilyHandedOut },
  })
}

async function setSendReviewRequest(jobId: string, sendReviewRequest: boolean) {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__SET_SEND_REVIEW_REQUEST, jobId),
    method: "PATCH",
    data: { sendReviewRequest },
  })
}

async function setPickupReminderPause(jobId: string, pausedUntil: Date | null) {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__SET_PICKUP_REMINDER_PAUSE, jobId),
    method: "PATCH",
    data: { pausedUntil },
  })
}

async function updateAssignedMechanic(
  jobId: string,
  assignedMechanicId: string | null,
) {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_ASSIGNED_MECHANIC, jobId),
    method: "PATCH",
    data: { assignedMechanicId },
  })
}

async function updateReceptionMechanic(
  jobId: string,
  receptionMechanicId: string,
) {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_RECEPTION_MECHANIC, jobId),
    method: "PATCH",
    data: { receptionMechanicId },
  })
}

async function updateInspectionMechanic(
  jobId: string,
  inspectionMechanicId: string,
) {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_INSPECTION_MECHANIC, jobId),
    method: "PATCH",
    data: { inspectionMechanicId },
  })
}

async function getIsDoneMessage(jobId: string): Promise<string> {
  return await request<string>({
    url: populateRoute(Route.JOB_CONTROLLER__GET_IS_DONE_MESSAGE, jobId),
    method: "GET",
  })
}

export const IS_DONE_MESSAGE_QUERY_KEY = "is-done-message"

export const getIsDoneMessageQueryOptions = (jobId: string) =>
  queryOptions({
    queryFn: () => getIsDoneMessage(jobId),
    queryKey: [IS_DONE_MESSAGE_QUERY_KEY, jobId],
  })

async function getReceptionConfirmationMessage(jobId: string): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.JOB_CONTROLLER__GET_RECEPTION_CONFIRMATION_MESSAGE,
      jobId,
    ),
    method: "GET",
  })
}

export const RECEPTION_CONFIRMATION_MESSAGE_QUERY_KEY =
  "bike-collected-confirmation"

export const getReceptionConfirmationMessageQueryOptions = (jobId: string) =>
  queryOptions({
    queryFn: () => getReceptionConfirmationMessage(jobId),
    queryKey: [RECEPTION_CONFIRMATION_MESSAGE_QUERY_KEY, jobId],
  })

async function getPriceProposalReminderMessage(jobId: string): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.JOB_CONTROLLER__GET_PRICE_PROPOSAL_REMINDER_MESSAGE,
      jobId,
    ),
    method: "GET",
  })
}

export const PRICE_PROPOSAL_REMINDER_MESSAGE_QUERY_KEY =
  "price-proposal-reminder-message"

export const getPriceProposalReminderMessageQueryOptions = (jobId: string) =>
  queryOptions({
    queryFn: () => getPriceProposalReminderMessage(jobId),
    queryKey: [PRICE_PROPOSAL_REMINDER_MESSAGE_QUERY_KEY, jobId],
  })

async function getAddonOrderReminderMessage(jobId: string): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.JOB_CONTROLLER__GET_ADDON_ORDER_REMINDER_MESSAGE,
      jobId,
    ),
    method: "GET",
  })
}

export const ADDON_ORDER_REMINDER_MESSAGE_QUERY_KEY =
  "addon-order-reminder-message"

export const getAddonOrderReminderMessageQueryOptions = (jobId: string) =>
  queryOptions({
    queryFn: () => getAddonOrderReminderMessage(jobId),
    queryKey: [ADDON_ORDER_REMINDER_MESSAGE_QUERY_KEY, jobId],
  })

async function getStoreAddonOrderReminderMessage(
  storeId: string,
): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.JOB_CONTROLLER__GET_STORE_ADDON_ORDER_REMINDER_MESSAGE,
      storeId,
    ),
    method: "GET",
  })
}

export const getStoreAddonOrderReminderMessageQueryOptions = (
  storeId: string,
) =>
  queryOptions({
    queryFn: () => getStoreAddonOrderReminderMessage(storeId),
    queryKey: [ADDON_ORDER_REMINDER_MESSAGE_QUERY_KEY, "store", storeId],
    gcTime: 0,
  })

async function getBikeDoneReminderMessage(jobId: string): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.JOB_CONTROLLER__GET_BIKE_DONE_REMINDER_MESSAGE,
      jobId,
    ),
    method: "GET",
  })
}

export const BIKE_DONE_REMINDER_MESSAGE_QUERY_KEY = "bike-done-reminder-message"

export const getBikeDoneReminderMessageQueryOptions = (jobId: string) =>
  queryOptions({
    queryFn: () => getBikeDoneReminderMessage(jobId),
    queryKey: [BIKE_DONE_REMINDER_MESSAGE_QUERY_KEY, jobId],
  })

async function getStorePriceProposalReminderMessage(
  storeId: string,
): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.JOB_CONTROLLER__GET_STORE_PRICE_PROPOSAL_REMINDER_MESSAGE,
      storeId,
    ),
    method: "GET",
  })
}

export const getStorePriceProposalReminderMessageQueryOptions = (
  storeId: string,
) =>
  queryOptions({
    queryFn: () => getStorePriceProposalReminderMessage(storeId),
    queryKey: [PRICE_PROPOSAL_REMINDER_MESSAGE_QUERY_KEY, "store", storeId],
    gcTime: 0,
  })

async function getStoreBikeDoneReminderMessage(
  storeId: string,
): Promise<string> {
  return await request<string>({
    url: populateRoute(
      Route.JOB_CONTROLLER__GET_STORE_BIKE_DONE_REMINDER_MESSAGE,
      storeId,
    ),
    method: "GET",
  })
}

export const getStoreBikeDoneReminderMessageQueryOptions = (storeId: string) =>
  queryOptions({
    queryFn: () => getStoreBikeDoneReminderMessage(storeId),
    queryKey: [BIKE_DONE_REMINDER_MESSAGE_QUERY_KEY, "store", storeId],
    gcTime: 0,
  })

export const useJobStatusMutation = (jobId: string) => {
  const queryClient = useQueryClient()
  const translateApiError = useTranslateApiError()
  const t = useTranslations("alfred.lib.jobs")

  return useMutation({
    mutationFn: (data: SetJobStatus) => setJobStatus(jobId, data),
    onError: (error) => {
      toast({
        title: t("statusUpdateFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
    onSettled: async () =>
      queryClient.invalidateQueries(jobQueries.detail(jobId)),
  })
}

export const useTemporarilyHandedOutMutation = (
  jobId: string,
  onComplete?: () => void,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (isTemporarilyHandedOut: boolean) =>
      setIsTemporarilyHandedOut(jobId, isTemporarilyHandedOut),
    onSettled: async () => {
      onComplete?.()
      await queryClient.invalidateQueries(jobQueries.detail(jobId))
      await queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
    },
  })
}

export const useSendReviewRequestMutation = (jobId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (sendReviewRequest: boolean) =>
      setSendReviewRequest(jobId, sendReviewRequest),
    onSettled: async () => {
      await queryClient.invalidateQueries(jobQueries.detail(jobId))
    },
  })
}

export const useSetPickupReminderPauseMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      pausedUntil,
    }: {
      jobId: string
      pausedUntil: Date | null
    }) => setPickupReminderPause(jobId, pausedUntil),
    onSettled: async (_data, _error, { jobId }) => {
      await queryClient.invalidateQueries(jobQueries.detail(jobId))
      await queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
    },
  })
}

export const useUpdateAssignedMechanicMutation = (jobId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (assignedMechanicId: string | null) =>
      updateAssignedMechanic(jobId, assignedMechanicId),
    onSettled: async () => {
      await queryClient.invalidateQueries(jobQueries.detail(jobId))
    },
  })
}

export const useUpdateReceptionMechanicMutation = (jobId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (receptionMechanicId: string) =>
      updateReceptionMechanic(jobId, receptionMechanicId),
    onSettled: async () => {
      await queryClient.invalidateQueries(jobQueries.detail(jobId))
    },
  })
}

export const useUpdateInspectionMechanicMutation = (jobId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (inspectionMechanicId: string) =>
      updateInspectionMechanic(jobId, inspectionMechanicId),
    onSettled: async () => {
      await queryClient.invalidateQueries(jobQueries.detail(jobId))
    },
  })
}

const parseReminderDates = (
  reminder: JobReminder | null,
): JobReminder | null =>
  reminder
    ? {
        ...reminder,
        waitingSince: reminder.waitingSince
          ? new Date(reminder.waitingSince)
          : null,
        lastReminderSentAt: reminder.lastReminderSentAt
          ? new Date(reminder.lastReminderSentAt)
          : null,
      }
    : null

const parseJobDates = (job: ExtendedJob): ExtendedJob => {
  return {
    ...job,
    events: job.events.map((event) => ({
      ...event,
      createdAt: new Date(event.createdAt),
    })),
    messages: job.messages.map((message) =>
      "scheduledSendAt" in message
        ? {
            ...message,
            createdAt: new Date(message.createdAt),
            scheduledSendAt: message.scheduledSendAt
              ? new Date(message.scheduledSendAt)
              : null,
          }
        : { ...message, createdAt: new Date(message.createdAt) },
    ),
    handInDate: new Date(job.handInDate),
    expectedDeliveryDate: job.expectedDeliveryDate
      ? new Date(job.expectedDeliveryDate)
      : null,
    createdAt: new Date(job.createdAt),
    updatedAt: new Date(job.updatedAt),
    proposal: job.proposal
      ? { createdAt: new Date(job.proposal.createdAt) }
      : null,
    reminder: parseReminderDates(job.reminder),
  }
}

const fetchJobsBetweenDates = async (
  storeId: string,
  start: Date,
  end: Date,
) => {
  const startKey = DateUtils.getDateKey(start)
  const endKey = DateUtils.getDateKey(end)

  const jobs = await request<JobSummary[]>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_MANY_BETWEEN_DATES, storeId),
    params: {
      start: startKey,
      end: endKey,
    },
    method: "GET",
  })

  return jobs.map((job) => ({
    ...job,
    handInDate: new Date(job.handInDate),
  }))
}

export const mapJobSummaryDates = (job: JobSummary): JobSummary => ({
  ...job,
  handInDate: new Date(job.handInDate),
  finishedAt: job.finishedAt ? new Date(job.finishedAt) : null,
  proposalSentAt: job.proposalSentAt ? new Date(job.proposalSentAt) : null,
  reminder: parseReminderDates(job.reminder),
  lastAddonOrderSentAt: job.lastAddonOrderSentAt
    ? new Date(job.lastAddonOrderSentAt)
    : null,
  collectedAt: job.collectedAt ? new Date(job.collectedAt) : null,
  proposalAcceptedAt: job.proposalAcceptedAt
    ? new Date(job.proposalAcceptedAt)
    : null,
  updatedAt: new Date(job.updatedAt),
})

const fetchSummariesByStatus = async (
  storeId: string,
  statusList: JobStatus[],
): Promise<JobSummary[]> => {
  const jobs = await request<JobSummary[]>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_SUMMARIES_BY_STATUS, storeId),
    params: {
      statuses: JSON.stringify(statusList),
    },
    method: "GET",
  })

  return jobs.map(mapJobSummaryDates)
}

const fetchWaitingForResponse = async (
  storeId: string,
): Promise<JobSummary[]> => {
  const jobs = await request<JobSummary[]>({
    url: populateRoute(
      Route.JOB_CONTROLLER__FIND_WAITING_FOR_RESPONSE,
      storeId,
    ),
    method: "GET",
  })

  return jobs.map(mapJobSummaryDates)
}

const fetchReadyForService = async (storeId: string): Promise<JobSummary[]> => {
  const jobs = await request<JobSummary[]>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_READY_FOR_SERVICE, storeId),
    method: "GET",
  })

  return jobs.map(mapJobSummaryDates)
}

const fetchWaitingForDelivery = async (
  storeId: string,
): Promise<JobSummary[]> => {
  const jobs = await request<JobSummary[]>({
    url: populateRoute(
      Route.JOB_CONTROLLER__FIND_WAITING_FOR_DELIVERY,
      storeId,
    ),
    method: "GET",
  })

  return jobs.map(mapJobSummaryDates)
}

interface JobListQuery {
  storeId: string
  filters: JobFilters
}

// All null → every company job in the store ("Alla företag").
interface CompanyJobsSelection {
  companyId: string | null
  departmentId: string | null
  companyContactPersonId: string | null
}

interface CompanyJobsFilters {
  statuses: CompanyJobsStatusFilterValue[]
  statusFilterMode: CompanyJobsStatusFilterMode
  invoicedFilter: CompanyJobsInvoicedFilter
}

const COMPANY_JOBS_PAGE_SIZE = 50

const companyJobsSelectionParams = (
  selection: CompanyJobsSelection,
): Record<string, string> => {
  const params: Record<string, string> = {}
  if (selection.companyId) params.companyId = selection.companyId
  if (selection.departmentId) params.departmentId = selection.departmentId
  if (selection.companyContactPersonId)
    params.companyContactPersonId = selection.companyContactPersonId
  return params
}

async function fetchJobsByCompany(
  storeId: string,
  selection: CompanyJobsSelection,
  filters: CompanyJobsFilters,
  page: number,
): Promise<CompanyJobsPage> {
  const res = await request<CompanyJobsPage>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_MANY_BY_COMPANY, storeId),
    params: {
      ...companyJobsSelectionParams(selection),
      page,
      size: COMPANY_JOBS_PAGE_SIZE,
      invoicedFilter: filters.invoicedFilter,
      statuses: filters.statuses.join(","),
      statusFilterMode: filters.statusFilterMode,
    },
    method: "GET",
  })

  return {
    ...res,
    jobs: res.jobs.map(mapJobSummaryDates),
  }
}

async function fetchCompanyJobCounts(
  storeId: string,
  selection: CompanyJobsSelection,
): Promise<CompanyJobCounts> {
  return request<CompanyJobCounts>({
    url: populateRoute(Route.JOB_CONTROLLER__COMPANY_JOB_COUNTS, storeId),
    params: companyJobsSelectionParams(selection),
    method: "GET",
  })
}

async function fetchCompanyJobFilterOptions(
  storeId: string,
): Promise<CompanyJobFilterOptions> {
  return request<CompanyJobFilterOptions>({
    url: populateRoute(
      Route.JOB_CONTROLLER__COMPANY_JOB_FILTER_OPTIONS,
      storeId,
    ),
    method: "GET",
  })
}

async function fetchJobsByIds(
  storeId: string,
  jobIds: string[],
): Promise<JobSummary[]> {
  if (jobIds.length === 0) return []
  const jobs = await request<JobSummary[]>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_MANY_BY_IDS, storeId),
    params: { jobIds: jobIds.join(",") },
    method: "GET",
  })
  return jobs.map(mapJobSummaryDates)
}

async function fetchJobsByConnectedJobsGroup(
  storeId: string,
  groupId: string,
): Promise<JobSummary[]> {
  const jobs = await request<JobSummary[]>({
    url: populateRoute(Route.CONNECTED_JOBS_GROUP__FIND_JOBS, groupId, storeId),
    method: "GET",
  })
  return jobs.map(mapJobSummaryDates)
}

async function fetchJobsByCustomer(
  storeId: string,
  phone?: string,
  email?: string,
  excludeJobId?: string,
): Promise<ExtendedJob[]> {
  const params: Record<string, string> = {}

  if (phone) params.phone = phone
  if (email) params.email = email
  if (excludeJobId) params.excludeJobId = excludeJobId

  const jobs = await request<ExtendedJob[]>({
    url: populateRoute(Route.JOB_CONTROLLER__FIND_BY_CUSTOMER, storeId),
    params,
    method: "GET",
  })

  return jobs.map((job) => parseJobDates(job))
}

interface JobSearchParams {
  storeId: string
  query: string
  filters: JobSearchFilter[]
  fromDate?: Date
  toDate?: Date
}

async function searchJobs(params: JobSearchParams): Promise<JobSummary[]> {
  const queryParams: Record<string, string> = {
    query: params.query,
  }

  if (params.filters.length > 0) {
    queryParams.filters = params.filters.join(",")
  }

  if (params.fromDate) {
    queryParams.fromDate = params.fromDate.toISOString()
  }

  if (params.toDate) {
    queryParams.toDate = params.toDate.toISOString()
  }

  const jobs = await request<JobSummary[]>({
    url: populateRoute(Route.JOB_CONTROLLER__SEARCH, params.storeId),
    params: queryParams,
    method: "GET",
  })

  return jobs.map((job) => ({
    ...job,
    handInDate: new Date(job.handInDate),
  }))
}

const jobQueries = {
  all: () => ["jobs"],
  lists: () => [...jobQueries.all(), "list"],
  list: ({ storeId, filters }: JobListQuery) =>
    queryOptions({
      queryKey: [...jobQueries.lists(), filters],
      queryFn: () => fetchJobs(storeId, filters),
    }),
  details: () => [...jobQueries.all(), "detail"],
  detail: (id: string) =>
    queryOptions({
      queryKey: [...jobQueries.details(), id],
      queryFn: () => findOneJobById(id),
    }),
  // Nested under the job's detail key, so anything that refreshes the job
  // (a status change, a pause, a sent message) refreshes this too.
  pickupReminders: (id: string) =>
    queryOptions({
      queryKey: [...jobQueries.details(), id, "pickup-reminders"],
      queryFn: () => fetchPickupReminders(id),
    }),
  storeAccess: (id: string) =>
    queryOptions({
      queryKey: [...jobQueries.all(), "store-access", id],
      queryFn: () => findJobStoreAccess(id),
      // A 404 means "no access or missing" — retrying only delays the toast.
      retry: false,
    }),
  betweenDates: (storeId: string, start: Date, end: Date) => {
    const startDate = DateUtils.getStartOfDay(start)
    const endDate = DateUtils.getEndOfDay(end)
    return queryOptions({
      queryKey: [
        ...jobQueries.all(),
        "betweenDates",
        storeId,
        startDate,
        endDate,
      ],
      queryFn: () => fetchJobsBetweenDates(storeId, startDate, endDate),
    })
  },
  byCustomer: (
    storeId: string,
    phone?: string,
    email?: string,
    excludeJobId?: string,
  ) =>
    queryOptions({
      queryKey: [
        ...jobQueries.all(),
        "by-customer",
        storeId,
        phone,
        email,
        excludeJobId,
      ],
      queryFn: () => fetchJobsByCustomer(storeId, phone, email, excludeJobId),
    }),
  search: (params: JobSearchParams) =>
    queryOptions({
      queryKey: [
        ...jobQueries.all(),
        "search",
        params.storeId,
        params.query,
        params.filters,
        params.fromDate?.toISOString(),
        params.toDate?.toISOString(),
      ],
      queryFn: () => searchJobs(params),
    }),
  byStatus: (storeId: string, statusList: JobStatus[]) =>
    queryOptions({
      queryKey: [...jobQueries.all(), "by-status", storeId, statusList],
      queryFn: () =>
        fetchJobs(storeId, {
          page: 1,
          size: 200,
          statusList,
        }),
    }),
  summariesByStatus: (storeId: string, statusList: JobStatus[]) =>
    queryOptions({
      queryKey: [
        ...jobQueries.all(),
        "summaries-by-status",
        storeId,
        statusList,
      ],
      queryFn: () => fetchSummariesByStatus(storeId, statusList),
    }),
  waitingForResponse: (storeId: string) =>
    queryOptions({
      queryKey: [...jobQueries.all(), "waiting-for-response", storeId],
      queryFn: () => fetchWaitingForResponse(storeId),
    }),
  readyForService: (storeId: string) =>
    queryOptions({
      queryKey: [...jobQueries.all(), "ready-for-service", storeId],
      queryFn: () => fetchReadyForService(storeId),
    }),
  waitingForDelivery: (storeId: string) =>
    queryOptions({
      queryKey: [...jobQueries.all(), "waiting-for-delivery", storeId],
      queryFn: () => fetchWaitingForDelivery(storeId),
    }),
  byCompany: (
    storeId: string,
    selection: CompanyJobsSelection,
    filters: CompanyJobsFilters,
  ) =>
    infiniteQueryOptions({
      queryKey: [
        ...jobQueries.all(),
        "by-company",
        storeId,
        selection.companyId,
        selection.departmentId,
        selection.companyContactPersonId,
        filters.invoicedFilter,
        filters.statusFilterMode,
        filters.statuses,
      ],
      queryFn: ({ pageParam }) =>
        fetchJobsByCompany(storeId, selection, filters, pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.hasMore ? allPages.length + 1 : undefined,
    }),
  companyJobCounts: (storeId: string, selection: CompanyJobsSelection) =>
    queryOptions({
      queryKey: [
        ...jobQueries.all(),
        "company-job-counts",
        storeId,
        selection.companyId,
        selection.departmentId,
        selection.companyContactPersonId,
      ],
      queryFn: () => fetchCompanyJobCounts(storeId, selection),
    }),
  // Lives under jobs so invoice, company and job mutations (which all
  // invalidate ["jobs"]) refresh the uninvoiced counts.
  companyFilterOptions: (storeId: string) =>
    queryOptions({
      queryKey: [...jobQueries.all(), "company-filter-options", storeId],
      queryFn: () => fetchCompanyJobFilterOptions(storeId),
    }),
  byIds: (storeId: string, jobIds: string[]) =>
    queryOptions({
      queryKey: [
        ...jobQueries.all(),
        "by-ids",
        storeId,
        [...jobIds].sort().join(","),
      ],
      queryFn: () => fetchJobsByIds(storeId, jobIds),
      enabled: jobIds.length > 0,
    }),
  byConnectedJobsGroup: (storeId: string, groupId: string) =>
    queryOptions({
      queryKey: [...jobQueries.all(), "connected-jobs-group", storeId, groupId],
      queryFn: () => fetchJobsByConnectedJobsGroup(storeId, groupId),
    }),
}

async function updateLeasingId(
  jobId: string,
  leasingId: string | null,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_LEASING_ID, jobId),
    method: "PATCH",
    data: { leasingId } satisfies UpdateLeasingId,
  })
}

async function updatePhysicalTag(
  jobId: string,
  physicalTag: number | null,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_PHYSICAL_TAG, jobId),
    method: "PATCH",
    data: { physicalTag },
  })
}

async function updateSize(jobId: string, size: number): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_SIZE, jobId),
    method: "PATCH",
    data: { size },
  })
}

async function updateHandInDate(
  jobId: string,
  handInDate: Date,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_HAND_IN_DATE, jobId),
    method: "PATCH",
    data: { handInDate },
  })
}

async function getAvailablePhysicalTags(
  storeId: string,
  serviceEventId?: string,
): Promise<number[]> {
  const params: Record<string, string> = {}
  if (serviceEventId) {
    params.serviceEventId = serviceEventId
  }

  return await request<number[]>({
    url: populateRoute(
      Route.JOB_CONTROLLER__GET_AVAILABLE_PHYSICAL_TAGS,
      storeId,
    ),
    params,
    method: "GET",
  })
}

export const useUpdateLeasingIdMutation = (
  jobId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (leasingId: string | null) => updateLeasingId(jobId, leasingId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: jobQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      onSuccess?.()
    },
  })
}

export const useUpdatePhysicalTagMutation = (
  jobId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (physicalTag: number | null) =>
      updatePhysicalTag(jobId, physicalTag),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: jobQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      onSuccess?.()
    },
  })
}

export const useUpdateSizeMutation = (
  jobId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (size: number) => updateSize(jobId, size),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: jobQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      onSuccess?.()
    },
  })
}

export const useUpdateHandInDateMutation = (
  jobId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (handInDate: Date) => updateHandInDate(jobId, handInDate),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: jobQueries.detail(jobId).queryKey,
      })
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      onSuccess?.()
    },
  })
}

export const availablePhysicalTagsQueries = {
  all: () => ["available-physical-tags"] as const,
  byStore: (storeId: string, serviceEventId?: string) =>
    queryOptions({
      queryKey: [
        ...availablePhysicalTagsQueries.all(),
        storeId,
        serviceEventId,
      ],
      queryFn: () => getAvailablePhysicalTags(storeId, serviceEventId),
    }),
}

// Bulk update functions
async function bulkUpdateStatus(
  storeId: string,
  data: BulkUpdateStatus,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__BULK_UPDATE_STATUS, storeId),
    method: "PATCH",
    data,
  })
}

async function bulkUpdateAssignedMechanic(
  storeId: string,
  data: BulkUpdateAssignedMechanic,
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.JOB_CONTROLLER__BULK_UPDATE_ASSIGNED_MECHANIC,
      storeId,
    ),
    method: "PATCH",
    data,
  })
}

async function bulkUpdateHandInDate(
  storeId: string,
  data: BulkUpdateHandInDate,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__BULK_UPDATE_HAND_IN_DATE, storeId),
    method: "PATCH",
    data,
  })
}

export const useBulkUpdateStatusMutation = (
  storeId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()
  const translateApiError = useTranslateApiError()
  const t = useTranslations("alfred.lib.jobs")

  return useMutation({
    mutationFn: (data: BulkUpdateStatus) => bulkUpdateStatus(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      onSuccess?.()
    },
    onError: (error) => {
      toast({
        title: t("statusUpdateFailed"),
        description: translateApiError(error),
        variant: "destructive",
      })
    },
  })
}

export const useBulkUpdateAssignedMechanicMutation = (
  storeId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()
  const t = useTranslations("alfred.lib.jobs")
  const tRoot = useTranslations()

  return useMutation({
    mutationFn: (data: BulkUpdateAssignedMechanic) =>
      bulkUpdateAssignedMechanic(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      onSuccess?.()
    },
    onError: () => {
      toast({
        title: t("mechanicChangeFailed"),
        description: tRoot("ui.errors.generic"),
        variant: "destructive",
      })
    },
  })
}

export const useBulkUpdateHandInDateMutation = (
  storeId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BulkUpdateHandInDate) =>
      bulkUpdateHandInDate(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      onSuccess?.()
    },
  })
}

async function updateJobCompanyContactPerson(
  jobId: string,
  data: { companyContactPersonId: string | null },
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.JOB_CONTROLLER__UPDATE_COMPANY_CONTACT_PERSON,
      jobId,
    ),
    method: "PATCH",
    data,
  })
}

async function updateJobReference(
  jobId: string,
  data: UpdateReference,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_REFERENCE, jobId),
    method: "PATCH",
    data,
  })
}

export const useUpdateJobCompanyContactPersonMutation = (jobId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { companyContactPersonId: string | null }) =>
      updateJobCompanyContactPerson(jobId, data),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["job", jobId] }),
        queryClient.invalidateQueries({ queryKey: ["jobs"] }),
        queryClient.invalidateQueries({ queryKey: ["calendar"] }),
      ])
    },
  })
}

export const useUpdateJobReferenceMutation = (jobId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpdateReference) => updateJobReference(jobId, data),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["job", jobId] }),
        queryClient.invalidateQueries({ queryKey: ["jobs"] }),
        queryClient.invalidateQueries({ queryKey: ["calendar"] }),
      ])
    },
  })
}

async function bulkMarkInvoiced(
  storeId: string,
  data: BulkMarkInvoiced,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__BULK_MARK_INVOICED, storeId),
    method: "PATCH",
    data,
  })
}

export const useBulkMarkInvoicedMutation = (
  storeId: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient()
  const t = useTranslations("alfred.lib.jobs")

  return useMutation({
    mutationFn: (data: BulkMarkInvoiced) => bulkMarkInvoiced(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] })
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      toast({ title: t("markedInvoiced") })
      onSuccess?.()
    },
    onError: (error) => {
      const description = describeBulkMarkInvoicedError(error, t)
      toast({
        title: t("markInvoicedFailed"),
        description,
        variant: "destructive",
      })
    },
  })
}

function describeBulkMarkInvoicedError(
  error: Error,
  t: ReturnType<typeof useTranslations<"alfred.lib.jobs">>,
): string | undefined {
  if (!(error instanceof AxiosError)) return undefined
  const data = error.response?.data
  if (!data || typeof data !== "object" || !("errorCode" in data)) {
    return undefined
  }
  if (data.errorCode === "JOBS_ALREADY_ON_INVOICE") {
    return t("markInvoicedErrors.jobsAlreadyOnInvoice")
  }
  if (data.errorCode === "MULTIPLE_COMPANIES") {
    return t("markInvoicedErrors.multipleCompanies")
  }
  if (data.errorCode === "NO_COMPANY") {
    return t("markInvoicedErrors.noCompany")
  }
  return undefined
}

async function updateJobPriceList(
  jobId: string,
  priceListId: string | null,
): Promise<void> {
  return await request<void>({
    url: populateRoute(Route.JOB_CONTROLLER__UPDATE_PRICE_LIST, jobId),
    method: "PATCH",
    data: { priceListId },
  })
}

export const useUpdateJobPriceListMutation = (jobId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (priceListId: string | null) =>
      updateJobPriceList(jobId, priceListId),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries(jobQueries.detail(jobId)),
        queryClient.invalidateQueries(addonOrderQueries.detail(jobId)),
      ])
    },
  })
}

export default jobQueries
